import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Tag, Typography } from 'antd';
import { ShoppingOutlined, StarFilled } from '@ant-design/icons';
import { getContentWrapperStyle } from '../templateStyles';

const { Title, Text, Paragraph } = Typography;
const { Meta } = Card;

export default function SectionProductGrid({ slug, section, siteInfo }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const products = section.content_data?.products || [];
  const brandColor = siteInfo?.brand_color || '#1890ff';
  const template = siteInfo?.site_template || 'modern';
  const columns = section.config?.columns || 3;
  const showFeaturedOnly = section.config?.show_featured_only || false;
  const maxItems = section.config?.max_items || 0;

  let items = showFeaturedOnly ? products.filter(p => p.is_featured) : products;
  if (maxItems > 0) items = items.slice(0, maxItems);

  if (items.length === 0) return null;

  const colSpan = { xs: 24, sm: 12, md: Math.floor(24 / columns), lg: Math.floor(24 / columns) };

  const isMinimal = template === 'minimal';
  const isClassic = template === 'classic';

  const cardRadius = isMinimal ? 0 : isClassic ? 4 : 12;
  const cardShadow = isMinimal ? 'none' : isClassic ? '0 2px 8px rgba(0,0,0,0.06)' : undefined;
  const cardBorder = isMinimal ? '1px solid #e8e8e8' : isClassic ? '1px solid #f0f0f0' : undefined;
  const contentFont = getContentWrapperStyle(template, brandColor);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 24px', ...contentFont }}>
      <Title level={2} style={{
        textAlign: isMinimal ? 'left' : 'center',
        marginBottom: 8,
        letterSpacing: isMinimal ? 1 : 0,
        fontWeight: isMinimal ? 300 : 600,
        borderBottom: isMinimal ? `2px solid ${brandColor}` : 'none',
        paddingBottom: isMinimal ? 8 : 0,
        display: isMinimal ? 'inline-block' : 'block',
      }}>
        {section.title || t('sections.productGrid.title')}
      </Title>
      {section.subtitle && (
        <Paragraph style={{ textAlign: isMinimal ? 'left' : 'center', color: '#666', marginBottom: 32 }}>
          {section.subtitle}
        </Paragraph>
      )}
      <Row gutter={[20, 20]}>
        {items.map(p => (
          <Col key={p.id} {...colSpan}>
            <Card
              hoverable
              onClick={() => navigate(`/s/${slug}/products/${p.id}`)}
              style={{
                borderRadius: cardRadius,
                boxShadow: cardShadow,
                border: cardBorder,
                overflow: 'hidden',
              }}
              cover={
                p.thumbnail ? (
                  <div style={{ overflow: 'hidden', background: '#f5f5f5' }}>
                    <img alt={p.name} src={p.thumbnail} style={{ width: '100%', display: 'block' }} />
                  </div>
                ) : (
                  <div style={{
                    height: 180,
                    background: isMinimal ? '#f8f8f8'
                      : isClassic ? '#f5f5f5'
                      : `linear-gradient(135deg, ${brandColor}22, ${brandColor}44)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderBottom: isMinimal ? '1px solid #e8e8e8' : 'none',
                  }}>
                    <ShoppingOutlined style={{ fontSize: 48, color: isMinimal ? '#ccc' : brandColor }} />
                  </div>
                )
              }
              styles={{ body: { padding: 16 } }}
            >
              <Meta
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {p.is_featured && <StarFilled style={{ color: '#faad14', fontSize: 14 }} />}
                    <span style={{ fontSize: 14 }}>{p.name}</span>
                  </div>
                }
                description={
                  <div>
                    {p.organization && <Tag color="blue" style={{ marginBottom: 6 }}>{p.organization}</Tag>}
                    <Paragraph ellipsis={{ rows: 2 }} style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                      {p.short_description || ''}
                    </Paragraph>
                    {p.custom_price_note && <Text strong style={{ color: brandColor, fontSize: 13 }}>{p.custom_price_note}</Text>}
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}
